import React, { useState, useEffect, useCallback } from 'react';
import { generateMatchAnalysisSummary } from '../utils';

function MatchAnalysisSummary({ player1, player2, surface, comparisonResult, onSummaryGenerated, autoGenerate = true, getAiSummary, setAiSummary }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load existing summary on component mount
  useEffect(() => {
    if (getAiSummary && player1 && player2 && surface) {
      const existingSummary = getAiSummary(player1, player2, surface);
      if (existingSummary) {
        setSummary(existingSummary);
      }
    }
  }, [getAiSummary, player1, player2, surface]);

  const generateSummary = useCallback(async () => {
    if (!comparisonResult?.advanced?.factors) {
      setError("Données insuffisantes pour générer un résumé");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await generateMatchAnalysisSummary(player1, player2, surface, comparisonResult);
      setSummary(result);
      
      // Save to persistent storage
      if (setAiSummary) {
        setAiSummary(player1, player2, surface, result);
      }
      
      if (onSummaryGenerated) {
        onSummaryGenerated(result);
      }
    } catch (err) {
      setError("Erreur lors de la génération du résumé");
      console.error('Summary generation error:', err);
    } finally {
      setLoading(false);
    }
  }, [player1, player2, surface, comparisonResult, onSummaryGenerated]);

  // Auto-generate summary when component mounts if we have the required data and autoGenerate is true
  useEffect(() => {
    if (autoGenerate && comparisonResult?.advanced?.factors && !summary && !loading) {
      generateSummary();
    }
  }, [autoGenerate, comparisonResult, summary, loading, generateSummary]);

  if (!comparisonResult?.advanced?.factors) {
    return null;
  }

  return (
    <div style={{
      marginTop: 20,
      border: '1px solid #e0e0e0',
      borderRadius: 8,
      padding: 20,
      backgroundColor: '#f8f9fa'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 15
      }}>
        <h4 style={{
          margin: 0,
          color: '#2c3e50',
          fontSize: 16,
          fontWeight: 600
        }}>
          🤖 Analyse IA du Match - {surface}
        </h4>
        
        {!loading && (
          <button
            onClick={generateSummary}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: 6,
              backgroundColor: summary ? '#6c757d' : '#007bff',
              color: 'white',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transform: 'scale(1)',
            }}
            disabled={loading}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.backgroundColor = summary ? '#5a6268' : '#0056b3';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.backgroundColor = summary ? '#6c757d' : '#007bff';
            }}
          >
            {summary ? '🔄 Régénérer' : '✨ Générer l\'analyse'}
          </button>
        )}
      </div>

      {/* Show prominent button when no summary exists */}
      {!summary && !loading && !error && (
        <div style={{
          textAlign: 'center',
          padding: 35,
          backgroundColor: 'white',
          borderRadius: 12,
          border: '2px dashed #1976d2',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
          marginBottom: 15,
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative background pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            backgroundImage: 'radial-gradient(circle at 20% 50%, #1976d2 2px, transparent 2px), radial-gradient(circle at 80% 50%, #1976d2 2px, transparent 2px)',
            backgroundSize: '40px 40px'
          }}></div>
          
          <div style={{
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              fontSize: 56,
              marginBottom: 20,
              animation: 'bounce 2s infinite',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
            }}>
              🎯
            </div>
            <h4 style={{
              margin: '0 0 10px 0',
              color: '#1976d2',
              fontSize: 18,
              fontWeight: 600
            }}>
              Analyse IA Avancée
            </h4>
            <p style={{
              margin: '0 0 25px 0',
              color: '#495057',
              fontSize: 14,
              lineHeight: 1.5,
              maxWidth: '400px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              Obtenez une analyse détaillée avec évaluation de confiance pour ce match sur <strong>{surface.toLowerCase()}</strong>
            </p>
            <button
              onClick={generateSummary}
              style={{
                padding: '14px 28px',
                border: 'none',
                borderRadius: 25,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                color: 'white',
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 600,
                transition: 'all 0.3s ease',
                boxShadow: '0 6px 20px rgba(25,118,210,0.3)',
                transform: 'scale(1)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05) translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(25,118,210,0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1) translateY(0)';
                e.target.style.boxShadow = '0 6px 20px rgba(25,118,210,0.3)';
              }}
            >
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>🤖</span>
                Générer l'analyse IA
                <span>✨</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div style={{
          textAlign: 'center',
          padding: 30,
          backgroundColor: '#e3f2fd',
          borderRadius: 8,
          border: '1px solid #bbdefb',
          animation: 'fadeIn 0.5s ease-in'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 15,
            marginBottom: 15
          }}>
            <div style={{
              width: 24,
              height: 24,
              border: '3px solid #1976d2',
              borderTop: '3px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <div style={{
              fontSize: 32,
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              🤖
            </div>
            <div style={{
              width: 24,
              height: 24,
              border: '3px solid #1976d2',
              borderTop: '3px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite reverse'
            }}></div>
          </div>
          <div style={{
            color: '#1976d2',
            fontSize: 16,
            fontWeight: 600,
            marginBottom: 8
          }}>
            Génération de l'analyse en cours...
          </div>
          <div style={{
            color: '#666',
            fontSize: 13
          }}>
            L'IA analyse les statistiques sur {surface.toLowerCase()}
          </div>
          <div style={{
            marginTop: 15,
            display: 'flex',
            justifyContent: 'center',
            gap: 4
          }}>
            {[0, 1, 2].map(i => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  backgroundColor: '#1976d2',
                  borderRadius: '50%',
                  animation: `bounce 1.4s ease-in-out ${i * 0.16}s infinite both`
                }}
              />
            ))}
          </div>
        </div>
      )}

      {error && (
        <div style={{
          padding: 15,
          backgroundColor: '#ffebee',
          borderRadius: 6,
          border: '1px solid #ffcdd2',
          color: '#c62828',
          fontSize: 14
        }}>
          {error}
        </div>
      )}

      {summary && !loading && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: 12,
          border: '2px solid #e3f2fd',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          animation: 'fadeIn 0.5s ease-in'
        }}>
          {/* Header with confidence indicator */}
          <div style={{
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            padding: '15px 20px',
            borderBottom: '1px solid #e0e0e0'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h5 style={{
                margin: 0,
                color: '#1976d2',
                fontSize: 16,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🎯 Analyse Détaillée
              </h5>
              
              {summary.confidence && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  backgroundColor: 'white',
                  padding: '8px 12px',
                  borderRadius: '20px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <span style={{
                    fontSize: 13,
                    fontWeight: 'bold',
                    color: summary.confidence >= 80 ? '#28a745' : 
                           summary.confidence >= 65 ? '#f57c00' : '#dc3545'
                  }}>
                    Confiance: {summary.confidence}%
                  </span>
                  <div style={{
                    width: '50px',
                    height: '8px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${summary.confidence}%`,
                      height: '100%',
                      background: summary.confidence >= 80 ? 
                        'linear-gradient(90deg, #28a745, #34ce57)' : 
                        summary.confidence >= 65 ? 
                        'linear-gradient(90deg, #f57c00, #ff9800)' : 
                        'linear-gradient(90deg, #dc3545, #f44336)',
                      borderRadius: '4px',
                      transition: 'width 0.5s ease'
                    }}></div>
                  </div>
                  <span style={{
                    fontSize: 11,
                    color: '#666',
                    fontWeight: 'normal'
                  }}>
                    {summary.confidence >= 80 ? '🟢 Élevée' : 
                     summary.confidence >= 65 ? '🟡 Moyenne' : '🔴 Faible'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Main analysis content */}
          <div style={{
            padding: '20px',
            lineHeight: 1.7
          }}>
            <div style={{
              fontSize: 15,
              color: '#2c3e50',
              textAlign: 'justify',
              marginBottom: 15,
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
              {summary.summary}
            </div>

            {/* Key insights section */}
            <div style={{
              backgroundColor: '#f8f9fa',
              borderLeft: '4px solid #1976d2',
              padding: '12px 16px',
              borderRadius: '0 8px 8px 0',
              marginTop: 15
            }}>
              <div style={{
                fontSize: 13,
                color: '#495057',
                fontWeight: 500,
                marginBottom: 5
              }}>
                💡 Points Clés
              </div>
              <div style={{
                fontSize: 12,
                color: '#6c757d',
                lineHeight: 1.5
              }}>
                {summary.confidence >= 80 ? 
                  "Prédiction très fiable basée sur des données statistiques complètes et cohérentes." :
                  summary.confidence >= 65 ?
                  "Prédiction modérément fiable avec quelques incertitudes dans les données." :
                  "Prédiction incertaine - à considérer avec prudence en raison de données limitées."
                }
              </div>
            </div>
          </div>

          {/* Footer with timestamp and metadata */}
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '12px 20px',
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 11,
            color: '#6c757d'
          }}>
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              {summary.generated ? (
                <>
                  <span style={{ color: '#28a745' }}>🤖</span>
                  Analyse IA
                </>
              ) : (
                <>
                  <span style={{ color: '#ffc107' }}>⚙️</span>
                  Analyse automatique
                </>
              )}
            </span>
            
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <span>🕒</span>
              {new Date(summary.timestamp).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default MatchAnalysisSummary; 